// path: components/singleProduct/SingleProduct8.js
'use client'
import React, { useEffect, useState, useMemo } from 'react'
import Slider4 from './sliders/Slider4'
import ProductSlider1 from './sliders/ProductSlider1'
import Star from '../common/Star'
import Option1 from '../singleProduct/Option1'
import ShareComponent from '../common/ShareComponent'
import { toast } from 'react-toastify'
import { useContextElement } from '@/context/Context'
import DOMPurify from 'dompurify'
import styles from './SingleProduct8.module.css'
import productSliderStyles from './sliders/ProductSlider1'
import RelatedSlider from '@/components/singleProduct/RelatedSlider'
import SwatchGroup from './SwatchGroup'

/* ---------- utils ---------- */
const n = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d)
const clamp = (x, a, b) => Math.max(a, Math.min(x, b))
const pushDataLayerEvent = (eventName, ecommerce = null, extra = {}) => {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []

  if (ecommerce) {
    window.dataLayer.push({ ecommerce: null })
  }

  window.dataLayer.push({
    event: eventName,
    ...(ecommerce ? { ecommerce } : {}),
    ...extra,
  })
}

const ABS_URL = /^https?:\/\//i
const IMAGE_BASE =
  process.env.NEXT_PUBLIC_IMAGE_ORIGIN || 'http://192.168.193.33:25000'
const rewriteToImageBase = (name) => {
  if (!name) return ''
  try {
    let path = name
    if (ABS_URL.test(name)) path = new URL(name).pathname
    if (!path.startsWith('/')) path = `/${path}`
    return `${IMAGE_BASE}${path}`
  } catch {
    return `${IMAGE_BASE}${name.startsWith('/') ? '' : '/'}${name}`
  }
}

const normalizeForCartWithSku = (p, opt) => {
  return {
    ProductID: p.ProductID,
    ProductName: p.ProductName,
    ParentID: p.ParentID,
    SuggestPrice: opt.SuggestPrice,
    Price: opt.Price,
    Cost: opt.Cost,
    PriceFromProduct: opt.Price,
    PictureData: p.PictureData,
    SkuID: opt?.SkuID ?? p.SkuID,
    SkuName: opt.SkuName,
    InventoryQty: opt.InventoryQty,
    LimitedQty: opt.LimitedQty,
    ProductOption: p.ProductOption ?? [],
    // 預購商品
    IsPreorder: p?.IsPreorder === true,
  }
}

function getOptValue(x) {
  if (x == null) return ''
  if (typeof x === 'string' || typeof x === 'number') return String(x).trim()
  return String(x.Value ?? x.Name ?? x.Text ?? x.Label ?? '').trim()
}

function findSkuByKey(product, key) {
  const list = Array.isArray(product?.ProductOption)
    ? product.ProductOption
    : []
  const k = String(key ?? '').trim()
  if (!k) return null
  return list.find((o) => String(o?.SkuName ?? '').trim() === k) || null
}

/* 後端加入購物車 */
async function addToCartApi({ ProductID, SkuID = 0, Qty = 1 }) {
  const res = await fetch('/api/product/add_product_to_cart', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ProductData: [
        {
          ProductID: Number(ProductID),
          SkuID: Number(SkuID) || 0,
          Qty: Number(Qty) || 1,
        },
      ],
    }),
  })
  if (!res.ok) throw new Error(`add_product_to_cart failed (${res.status})`)
  try {
    return await res.json()
  } catch {
    return { ok: true }
  }
}

/* ---------- 尺寸 swatches（橫向 flex 版） ---------- */
function SizeSwatches({ label, values = [], value, onChange }) {
  const splitSize = (v) => {
    const m = String(v).match(/^(.+?)[（(]\s*(.+?)\s*[)）]$/)
    return m ? [m[1], m[2]] : [v, '']
  }

  return (
    <div className="d-flex">
      <div style={{ width: '35%' }}>
        <label>{label}</label>
      </div>
      <div className="product-swatch text-swatches">
        <div className="swatch-list swatch-row">
          {values.map((v) => {
            const [main, sub] = splitSize(v)
            const active = value === v
            return (
              <button
                key={v}
                type="button"
                className={`swatch size-swatch ${active ? 'active' : ''}`}
                onClick={() => onChange?.(v)}
                aria-pressed={active}
              >
                <span className="main">{main}</span>
                {sub ? <span className="sub">{sub}</span> : null}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function ProductSaleProjects({ projects = [], className = '' }) {
  if (!Array.isArray(projects) || projects.length === 0) return null

  return (
    <div className={`${styles.saleProjectBox} ${className}`.trim()}>
      <h3 className={styles.saleProjectTitle}>商品活動</h3>
      <div className={styles.saleProjectList}>
        {projects.map((project, idx) => {
          const key = `${project?.PjtID ?? 'project'}-${idx}`
          return (
            <div key={key} className={styles.saleProjectItem}>
              <div className={styles.saleProjectName}>
                {project?.PjtName || '未命名活動'}
              </div>
              {project?.Remark ? (
                <div className={styles.saleProjectRemark}>{project.Remark}</div>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ---------- component ---------- */
export default function SingleProduct8({
  product: ssrProduct,
  storeInfo,
  products,
}) {
  const {
    cartProducts,
    setCartProducts,
    addProductToCart,
    wishList,
    toggleWishlist,
  } = useContextElement()

  const [product, setProduct] = useState(ssrProduct || null)
  const [quantity, setQuantity] = useState(1)
  const [maxQuantity, setMaxQuantity] = useState(999)
  const [skuName1, setSkuName1] = useState('') // 例如 顏色
  const [skuName2, setSkuName2] = useState('') // 例如 尺寸
  const [selectedOption, setSelectedOption] = useState(null)

  const [price, setPrice] = useState(0)
  const [salePrice, setSalePrice] = useState(0)

  const [skuImage, setSkuImage] = useState(null)

  const [cleanHTML, setCleanHTML] = useState('')

  useEffect(() => {
    if (!product?.ProductDetail) {
      setCleanHTML('')
      return
    }

    setCleanHTML(DOMPurify.sanitize(product.ProductDetail))
  }, [product?.ProductDetail])

  const opt1 = product?.ProductOption1 || null // { OptionType, Name, Data }
  const opt2 = product?.ProductOption2 || null
  const opt = product?.ProductOption // 所有的 Sku 資料

  const opt1Name = opt1?.Name || '選項一'
  const opt2Name = opt2?.Name || '選項二'
  const opt1List = Array.isArray(opt1?.Data) ? opt1.Data : []
  const opt2List = Array.isArray(opt2?.Data) ? opt2.Data : []

  // 預購商品
  const isPreorder = product?.IsPreorder === true

  const judgeIsAddWishList = (id) => {
    const isAdded = wishList.some((item) => item.ProductID === id)
    return isAdded ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314z" />
      </svg>
    ) : (
      <>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8 2.748l-.717-.737C5.6.281 2.514.878 1.4 3.053c-.523 1.023-.641 2.5.314 4.385.92 1.815 2.834 3.989 6.286 6.357 3.452-2.368 5.365-4.542 6.286-6.357.955-1.886.838-3.362.314-4.385C13.486.878 10.4.28 8.717 2.01L8 2.748zM8 15C-7.333 4.868 3.279-3.04 7.824 1.143c.06.055.119.112.176.171a3.12 3.12 0 0 1 .176-.17C12.72-3.042 23.333 4.867 8 15z" />
        </svg>
        <span>Add to Wishlist</span>
      </>
    )
  }

  /* 首次用 SSR，mounted 再打一次（Network 看得到） */
  // 如果要用要加上 MemberID
  // useEffect(() => {
  //   if (!ssrProduct?.ProductID) return
  //   const params = new URLSearchParams({
  //     StoreNumber: process.env.NEXT_PUBLIC_STORE_NUMBER ?? '',
  //     ProductID: String(ssrProduct.ProductID),
  //   })
  //   fetch(`/api/product/get_ProductDetail?${params.toString()}`, {
  //     cache: 'no-store',
  //   })
  //     .then((r) => r.json())
  //     .then((j) => setProduct(j?.Data || ssrProduct))
  //     .catch(() => setProduct(ssrProduct))
  // }, [ssrProduct?.ProductID])

  /* 預設抓第一個可售 SKU（僅用於初始價格/限制，不強制選規格） */
  function initFromProduct(p) {
    if (!p) return
    const list = Array.isArray(p.ProductOption) ? p.ProductOption : []
    const first =
      list.find((o) => n(o.InventoryQty, 1) > 0 && (o.Status ?? true)) ||
      list[0] ||
      null

    // 這裡不再強制把 first 當成已選規格（避免「需要選顏色/尺寸」時誤開啟加入購物車）
    setSelectedOption(null)

    const basePrice = n(first?.SuggestPrice, n(p.SuggestPrice, n(p.Price, 0)))
    const baseSale = n(first?.Price, n(p.Price, n(p.SuggestPrice, 0)))
    setPrice(basePrice)
    setSalePrice(baseSale)

    const stock = n(first?.InventoryQty, n(p.InventoryQty, 999))
    const limit = n(first?.LimitedQty, n(p.LimitedQty, 999))
    const max = Math.min(stock || 999, limit || 999)
    setMaxQuantity(max)
    setQuantity((q) => clamp(q || 1, 1, max))
  }

  const priceRange = useMemo(() => {
    if (!product?.ProductOption?.length) return null

    const suggestPrice = product.ProductOption.map((o) =>
      n(o.SuggestPrice, 0)
    ).filter((p) => p > 0)
    const prices = product.ProductOption.map((o) => n(o.Price, 0)).filter(
      (p) => p > 0
    )

    if (!prices.length) return null

    const min = Math.min(...prices)
    const max = Math.max(...prices)

    const minSuggest = Math.min(...suggestPrice)
    const maxSuggest = Math.max(...suggestPrice)

    return { min, max, minSuggest, maxSuggest }
  }, [product])

  const priceDisplay = useMemo(() => {
    if (!priceRange) return null

    const { min, max, minSuggest, maxSuggest } = priceRange

    const noDiscount = min === minSuggest && max === maxSuggest

    if (noDiscount) {
      if (minSuggest === maxSuggest) {
        return {
          type: 'single',
          value: minSuggest,
        }
      }

      return {
        type: 'suggest-range',
        min: minSuggest,
        max: maxSuggest,
      }
    }

    if (min === max && minSuggest === maxSuggest) {
      return {
        type: 'sale-single',
        suggest: minSuggest,
        price: min,
      }
    }

    return {
      type: 'sale-range',
      min,
      max,
      minSuggest,
      maxSuggest,
    }
  }, [priceRange])

  const currentSaleProjects = useMemo(() => {
    const selectedProjects = Array.isArray(selectedOption?.SaleProjects)
      ? selectedOption.SaleProjects
      : []

    if (selectedProjects.length > 0) {
      return selectedProjects
    }

    return Array.isArray(product?.SaleProjects) ? product.SaleProjects : []
  }, [product?.SaleProjects, selectedOption?.SaleProjects])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    })
  }, [product?.ProductID])

  const hasSelectedPrice = selectedOption && (n(price) > 0 || n(salePrice) > 0)

  useEffect(() => {
    initFromProduct(product)

    if (!product?.ProductOption) return

    // 原本這段你先留著也沒關係（目前沒有使用）
    const firstAvailable = product.ProductOption.find(
      (op) => op.InventoryQty > 0 && op.SafetyInventoryQty > 0 && op.Price > 0
    )

    if (firstAvailable) {
      const raw = firstAvailable.SkuName ?? ''
      const [name1, name2] = raw.split('*')
      // setSkuName1(name1?.trim() ?? '')
      // setSkuName2(name2?.trim() ?? '')
    }
  }, [product])

  // GTM / GA4 / FaceBook Pixel：瀏覽商品詳情
  useEffect(() => {
    if (!product?.ProductID) return

    const firstSku = Array.isArray(product.ProductOption)
      ? product.ProductOption[0]
      : null

    const itemPrice = Number(
      firstSku?.Price ?? product.Price ?? product.SuggestPrice ?? 0
    )

    pushDataLayerEvent('view_item', {
      currency: 'TWD',
      value: itemPrice,
      items: [
        {
          item_id: String(product.ProductID),
          item_name: product.ProductName || '',
          price: itemPrice,
          quantity: 1,
          item_variant: firstSku?.SkuID ? String(firstSku.SkuID) : '',
          is_preorder: Boolean(product.IsPreorder),
        },
      ],
    })

    // ✅ Facebook Pixel
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'ViewContent', {
        content_ids: [
          String(firstSku?.GoodListID || firstSku?.SkuID || product.ProductID),
        ],
        content_name: product.ProductName || '',
        content_type: 'product',
        value: itemPrice,
        currency: 'TWD',
      })
    }
  }, [product?.ProductID])

  /**
   * ✅ 自動預設：
   * 1) 顏色（Option1）永遠預設第一個
   * 2) 尺寸（Option2）只有在「總尺寸只有 1 個」且「該組合有庫存」才預設
   *    - 沒庫存 / 尺寸多個：不預設
   */

  function getSkuParts(skuName) {
    return String(skuName || '')
      .split('*')
      .map((x) => x.trim())
      .filter(Boolean)
  }

  function isSkuAvailable(sku) {
    if (!sku) return false

    if (isPreorder) {
      return n(sku.Price, 0) > 0 && (sku.Status ?? true)
    }

    return (
      n(sku.InventoryQty, 0) > 0 && n(sku.Price, 0) > 0 && (sku.Status ?? true)
    )
  }

  function findFirstAvailableOpt1(product, op1Data = []) {
    const skuList = Array.isArray(product?.ProductOption)
      ? product.ProductOption
      : []

    for (const item of op1Data) {
      const color = getOptValue(item)
      if (!color) continue

      const hasAvailableSku = skuList.some((sku) => {
        const [p0] = getSkuParts(sku?.SkuName)
        return p0 === color && isSkuAvailable(sku)
      })

      if (hasAvailableSku) return color
    }

    return ''
  }

  useEffect(() => {
    if (!product) return

    const isOp1 = !!product.IsProductOption1
    const isOp2 = !!product.IsProductOption2

    const op1Data = product.ProductOption1.Data
    const op2Data = product.ProductOption2.Data

    // 先決定顏色預設（但不覆蓋使用者已選）
    let nextSku1 = skuName1
    if (isOp1 && !nextSku1) {
      const firstAvailableColor = findFirstAvailableOpt1(product, op1Data)
      if (firstAvailableColor) nextSku1 = firstAvailableColor
    }

    // 尺寸只在「總尺寸只有 1」且「該 SKU 有庫存」才預設
    let nextSku2 = skuName2
    if (isOp2 && !nextSku2 && op2Data.length === 1) {
      const onlySize = getOptValue(op2Data[0])
      if (onlySize) {
        let key = ''
        if (isOp1) {
          if (nextSku1) key = `${nextSku1}*${onlySize}`
        } else {
          key = onlySize
        }

        const found = findSkuByKey(product, key)
        const hasStock =
          found && n(found.InventoryQty, 0) > 0 && (found.Status ?? true)
        if (hasStock) nextSku2 = onlySize
      }
    }

    // 一次把預設塞進去（避免多次重繪）
    if (isOp1 && !skuName1 && nextSku1) setSkuName1(nextSku1)
    if (isOp2 && !skuName2 && nextSku2) setSkuName2(nextSku2)
  }, [
    product?.ProductID,
    product?.IsProductOption1,
    product?.IsProductOption2,
    opt1List.length,
    opt2List.length,
    product?.ProductOption?.length,
  ])

  /* 由兩個選項組合出 SkuName，對應 SKU 帶出價格/數量限制 */
  useEffect(() => {
    if (!product) return

    const list = Array.isArray(product.ProductOption)
      ? product.ProductOption
      : []

    let key = ''
    if (product.IsProductOption1 && skuName1) key = skuName1
    if (product.IsProductOption2 && skuName2)
      key = key ? `${key}*${skuName2}` : skuName2

    // 沒選規格 → reset
    if (!key) {
      setSelectedOption(null)
      setSkuImage(null)

      setPrice(n(product.SuggestPrice, n(product.Price, 0)))
      setSalePrice(n(product.Price, n(product.SuggestPrice, 0)))
      const max0 = Math.min(
        n(product.InventoryQty, 999),
        n(product.LimitedQty, 999)
      )
      setMaxQuantity(max0)
      setQuantity((q) => clamp(q || 1, 1, max0))
      return
    }

    // 找到選項
    const found =
      list.find((o) => (o?.SkuName || '').trim() === key.trim()) || null

    // ✅ key 有值但找不到 SKU → 一律清掉（避免殘留上一個 selectedOption）
    if (!found) {
      setSelectedOption(null)
      setSkuImage(null)

      setPrice(n(product.SuggestPrice, n(product.Price, 0)))
      setSalePrice(n(product.Price, n(product.SuggestPrice, 0)))
      const max0 = Math.min(
        n(product.InventoryQty, 999),
        n(product.LimitedQty, 999)
      )
      setMaxQuantity(max0)
      setQuantity((q) => clamp(q || 1, 1, max0))
      return
    }

    setSelectedOption(found)
    setSkuImage(found.PicturePath)

    // 設價格
    const p1 = n(
      found?.SuggestPrice,
      n(product.SuggestPrice, n(product.Price, 0))
    )
    const p2 = n(found?.Price, n(product.Price, n(product.SuggestPrice, 0)))
    setPrice(p1)
    setSalePrice(p2)

    // 設庫存限制
    const stock = n(found?.InventoryQty, n(product.InventoryQty, 999))
    const limit = n(found?.LimitedQty, n(product.LimitedQty, 999))
    const max = Math.min(stock || 999, limit || 999)
    setMaxQuantity(max)

    const hit = cartProducts?.find(
      (c) => (c?.SkuName || '').trim() === key.trim()
    )
    const nextQty = hit?.Qty ?? 1
    setQuantity(nextQty)
  }, [skuName1, skuName2, product])

  /* 加入購物車 */
  const handleAddToCart = async () => {
    if (!product) return
    const opt =
      product.IsProductOption1 || product.IsProductOption2
        ? selectedOption
        : product.ProductOption?.[0] || null

    if (!opt) {
      toast.error('請先選擇商品選項')
      return
    }

    const safeQty = clamp(quantity, 1, maxQuantity)
    const exist = cartProducts.find(
      (x) => x.ProductID === product.ProductID && x.SkuID === opt.SkuID
    )
    const existedQty = exist?.Qty ?? 0
    if (safeQty > maxQuantity) {
      toast.warning(
        <p style={{ whiteSpace: 'pre-line' }}>
          <strong>無法加入購物車</strong>
          {`\n此商品購買上限: ${maxQuantity}，購物車已有: ${existedQty}`}
        </p>
      )
      return
    }

    try {
      await addProductToCart({
        ProductID: product.ProductID,
        updated: {
          ...normalizeForCartWithSku(product, opt),
          SkuID: opt.SkuID ?? 0,
          Qty: safeQty,
        },
      })
    } catch (e) {
      console.error(e)
    }
  }

  /* Slider 圖片 */
  const sliderImages = (product?.PictureData || []).map((p) => ({
    ...p,
    Name: p?.Name,
  }))

  // ✅ 手機版小圖只有 1 張：不顯示那排小圖（直接顯示單張）
  const isMobileSingleImage = sliderImages.length <= 1
  const mobileMainSrc = skuImage ? skuImage : sliderImages?.[0]?.Name

  return (
    <section className="product-single container">
      <div className="row">
        <div className="col-lg-7">
          {/* 手機版：只有 1 張圖就不要顯示縮圖列 */}
          <div className="d-lg-none sp8-mobile-slider">
            {isMobileSingleImage ? (
              <div className="sp8-mobile-single-image">
                {mobileMainSrc ? (
                  <img
                    src={mobileMainSrc}
                    alt={product?.ProductName || 'product'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: 12,
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <Slider4
                images={sliderImages}
                skuImage={skuImage}
                onClearSkuImage={() => setSkuImage(null)}
              />
            )}
          </div>

          {/* 桌機版：顯示 ProductSlider1 */}
          <div className="d-none d-lg-block">
            <div>
              <ProductSlider1
                images={sliderImages}
                skuImage={skuImage}
                onClearSkuImage={() => setSkuImage(null)}
              />
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <h1 className="product-single__name">
            {product?.ProductName}
            {isPreorder && ' (預購)'}
          </h1>
          <div className={styles.parentIdText}>{product.ParentID}</div>

          {/* <div className="product-single__rating">
            <div className="reviews-group d-flex">
              <Star stars={5} />
            </div>
          </div> */}

          <div className="product-single__price">
            {/* ① 尚未選 SKU → 顯示區間價 */}
            {!hasSelectedPrice && priceDisplay && (
              <div>
                {priceDisplay.type === 'single' && (
                  <span className="money current-price">
                    ${priceDisplay.value.toLocaleString()}
                  </span>
                )}

                {priceDisplay.type === 'suggest-range' && (
                  <span className="money current-price">
                    ${priceDisplay.min.toLocaleString()} ~ $
                    {priceDisplay.max.toLocaleString()}
                  </span>
                )}

                {priceDisplay.type === 'sale-single' && (
                  <>
                    <span className="money price-old">
                      ${priceDisplay.suggest.toLocaleString()}
                    </span>
                    <span className="money price-sale current-price">
                      ${priceDisplay.price.toLocaleString()}
                    </span>
                  </>
                )}

                {priceDisplay.type === 'sale-range' && (
                  <>
                    <span className="money price-old">
                      ${priceDisplay.minSuggest.toLocaleString()} ~ $
                      {priceDisplay.maxSuggest.toLocaleString()}
                    </span>
                    <span className="money price-sale current-price">
                      ${priceDisplay.min.toLocaleString()} ~ $
                      {priceDisplay.max.toLocaleString()}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* ② 已選 SKU → 顯示原價 / 特價 */}
            {hasSelectedPrice && (
              <>
                {n(salePrice) !== n(price) ? (
                  <>
                    <span className="money price price-old">
                      ${price.toLocaleString()}
                    </span>
                    <span className="money price-sale current-price">
                      ${salePrice.toLocaleString()}
                    </span>
                  </>
                ) : (
                  <span className="money current-price">
                    $
                    {n(salePrice).toLocaleString() || n(price).toLocaleString()}
                  </span>
                )}
              </>
            )}
          </div>

          <div style={{ fontSize: '16px', lineHeight: '1.4' }}>
            <pre>{product?.ProductFeatures}</pre>
          </div>

          <form onSubmit={(e) => e.preventDefault()}>
            <div className="product-single__swatches">
              {/* 選項一（例如：顏色） */}
              {product?.IsProductOption1 && (
                <SwatchGroup
                  mode="op1"
                  label={opt1Name}
                  list={opt1List}
                  value={skuName1}
                  onChange={(color) => {
                    setSkuName1(color)
                  }}
                  opt={opt}
                  isPreorder={isPreorder}
                />
              )}

              {/* 選項二（例如：尺寸） */}
              {product?.IsProductOption2 && (
                <SwatchGroup
                  mode="op2"
                  label={opt2Name}
                  list={opt2List}
                  selectedOpt={skuName1}
                  value={skuName2}
                  onChange={setSkuName2}
                  opt={opt}
                  isPreorder={isPreorder}
                />
              )}
            </div>

            {storeInfo?.CartButtonEnable && (
              <div className="product-single__addtocart">
                <div className="qty-control position-relative">
                  <input
                    type="number"
                    name="quantity"
                    value={quantity}
                    readOnly
                    min="1"
                    max={`${maxQuantity}`}
                    className="qty-control__number text-center"
                  />
                  <div
                    onClick={() => setQuantity((q) => (q - 1 <= 0 ? 1 : q - 1))}
                    className="qty-control__reduce"
                  >
                    -
                  </div>
                  <div
                    onClick={() =>
                      setQuantity((q) =>
                        q + 1 >= maxQuantity ? maxQuantity : q + 1
                      )
                    }
                    className="qty-control__increase"
                  >
                    +
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={
                    (product?.IsProductOption1 || product?.IsProductOption2) &&
                    !selectedOption?.SkuID
                  }
                  className={`btn btn-primary btn-addtocart js-open-aside ${(product?.IsProductOption1 || product?.IsProductOption2) && !selectedOption?.SkuID ? 'disabled' : 'enable'}`}
                >
                  加入購物車
                </button>
              </div>
            )}
          </form>

          {/* <div className="product-single__meta-info">
            <div className="meta-item text-red">
              <label>剩餘數量:</label>
              <span>
                {selectedOption?.InventoryQty ?? product?.InventoryQty}
              </span>
            </div>
          </div> */}

          <div className="product-single__addtolinks">
            {storeInfo?.WishlistButtonEnable && (
              <a
                href="#"
                className="add-to-wishlist"
                onClick={() => toggleWishlist(product)}
              >
                {judgeIsAddWishList(product.ProductID)}
              </a>
            )}
            <ShareComponent title={product?.ProductName} />
          </div>

          <div className="d-none d-lg-block">
            <ProductSaleProjects projects={currentSaleProjects} />
          </div>
        </div>
      </div>

      <div className="row d-flex justify-content-center mt-2">
        <div className="col-lg-8">
          <div className="d-lg-none">
            <ProductSaleProjects projects={currentSaleProjects} />
          </div>

          {cleanHTML !== null && cleanHTML !== '' && (
            <div className="detail-container">
              <h2 className="h3 text-uppercase mb-4 pb-xl-2 mb-xl-4">
                <strong>商品詳情</strong>
              </h2>
              <div
                className="detail-content"
                dangerouslySetInnerHTML={{ __html: cleanHTML }}
              />
            </div>
          )}

          {(product?.ProductSpecification || []).length !== 0 && (
            <div className={styles.productSpecBox}>
              <h2
                className={`h3 text-uppercase mb-4 pb-xl-2 mb-xl-4 ${styles.specTitleWrap}`}
              >
                <strong>商品規格</strong>
              </h2>

              <div className={styles.specTable}>
                {(product?.ProductSpecification || [])
                  .filter((x) => x.Title && x.Value)
                  .map((spec, idx) => (
                    <div key={idx} className={`row ${styles.specRow}`}>
                      <div className={`col-4 col-lg-3 ${styles.specTitle}`}>
                        {spec.Title}
                      </div>

                      <div className={`col-8 col-lg-9 ${styles.specValue}`}>
                        {spec.Value}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
          <RelatedSlider products={products} />
        </div>
      </div>

      {/* ---- 全域樣式：改為橫向排列 ---- */}
      <style jsx global>{`
        .product-single__short-desc pre {
          font-size: 18px !important; /* 原本更小，改大一點 */
          font-weight: 700 !important; /* 想小粗一點就 600 */
          line-height: 1.85 !important; /* 讓閱讀舒服一點 */
        }

        /* ✅ 下面紅框：商品詳情（detail-content）字大一點 + 變粗 */
        .product-single .detail-container .detail-content,
        .product-single .detail-container .detail-content p,
        .product-single .detail-container .detail-content li {
          font-size: 18px !important;
          font-weight: 700 !important;
          line-height: 1.85 !important;
        }
        .product-swatch .swatch-list,
        .product-swatch .swatch-list.swatch-row {
          display: flex !important;
          flex-wrap: wrap;
          gap: 10px;
          padding: 0;
          border: 0;
        }

        .product-swatch .size-swatch {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          width: 92px;
          height: 56px;
          padding: 6px 10px;
          border: 1px solid #e5e5e5;
          border-radius: 10px;
          background: #fff;
          line-height: 1.1;
          font-weight: 600;
          transition:
            border-color 0.15s ease,
            box-shadow 0.15s ease;
        }
        .product-swatch .size-swatch .main {
          font-size: 14px;
        }
        .product-swatch .size-swatch .sub {
          margin-top: 2px;
          font-size: 12px;
          color: #6c757d;
        }
        .product-swatch .size-swatch:hover {
          border-color: #222;
        }
        .product-swatch .size-swatch.active {
          border-color: #111;
          box-shadow: inset 0 0 0 1px #111;
        }

        .product-swatch.text-swatches .swatch-list {
          display: flex !important;
          flex-wrap: wrap;
          gap: 10px;
        }

        @media (max-width: 991.98px) {
          .product-single__name {
            margin-bottom: 6px !important;
          }
          .product-single__rating {
            margin-bottom: 6px !important;
          }
          .product-single__price {
            margin-bottom: 10px !important;
          }

          .product-single__short-desc {
            margin-top: 6px !important;
            margin-bottom: 16px !important;
          }
          .product-single__short-desc pre {
            margin: 0 !important;
            padding: 0 !important;
          }

          .product-single__swatches {
            margin-top: 8px !important;
          }
          .product-single__swatches .d-flex {
            margin-bottom: 10px !important;
          }

          .product-single__addtocart {
            display: flex !important;
            align-items: center;
            flex-wrap: nowrap;
            gap: 10px;
          }

          .product-single__addtocart .qty-control {
            flex: 0 0 112px;
            margin: 0 !important;
            margin-right: 14px !important;
          }

          .product-single__addtocart .btn-addtocart {
            flex: 1 1 0;
            min-width: 150px;
            white-space: nowrap;
            font-size: clamp(12px, 3.6vw, 14px);
            padding: clamp(8px, 2.6vw, 10px) clamp(12px, 3vw, 16px);
          }

          .product-single__addtocart .product-single__meta-info {
            flex: 0 0 88px;
            margin: 0 !important;
            padding: 0 !important;
          }

          .product-single__addtocart .product-single__meta-info .meta-item {
            margin: 0 !important;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
          }

          .product-single__addtocart .product-single__meta-info label,
          .product-single__addtocart .product-single__meta-info span {
            white-space: nowrap;
          }

          .product-single__addtocart .product-single__meta-info label {
            margin: 0 !important;
            font-size: 11px;
            line-height: 1.1;
            color: #666;
          }

          .product-single__addtocart .product-single__meta-info span {
            font-size: 13px;
            font-weight: 600;
            line-height: 1.1;
          }

          .sp8-mobile-slider {
            margin-bottom: 4px !important;
          }
          .product-single__name {
            margin-top: 0 !important;
          }
        }
      `}</style>
    </section>
  )
}
